import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import { useBaseDataLayerVariables } from './useBaseDataLayerVariables';
import dayjs from 'dayjs';

export interface DataLayerEvent {
  event?: string;
  [key: string]: unknown;
}

export interface EcommercePayload {
  items?: Record<string, unknown>[];
  [key: string]: unknown;
}
export interface EcommerceEvent extends DataLayerEvent {
  ecommerce?: EcommercePayload;
}

declare global {
  // eslint-disable-next-line no-var
  var dataLayer: Record<string, unknown>[] | undefined;
}

/**
 * Processes data layer object, normalizing values and handling special cases
 */
export const processDataLayerObject = (data: unknown, preserveUnderscore = true): unknown => {
  if (!data || typeof data !== 'object') return data;

  if (Array.isArray(data)) {
    return data.map((item) => processDataLayerObject(item, preserveUnderscore));
  }

  const processed: Record<string, unknown> = {};

  const record = data as Record<string, unknown>;

  for (const key in record) {
    if (record[key] === undefined || record[key] === null) continue;

    const preventNormalization = preserveUnderscore && key.startsWith('_');
    const normalizedKey = preventNormalization ? key.substring(1) : key;

    let normalizedValue: unknown;
    if (preventNormalization) {
      normalizedValue = record[key];
    } else if (typeof record[key] === 'object' && record[key] !== null) {
      normalizedValue = processDataLayerObject(record[key], preserveUnderscore);
    } else {
      normalizedValue = record[key];
    }

    processed[normalizedKey] = normalizedValue;
  }

  return processed;
};

/** Ensures dataLayer is initialized and doesn't execute on server */
const ensureDataLayerExists = (): boolean => {
  if (isServer()) return false;
  globalThis.dataLayer = globalThis.dataLayer || [];
  return true;
};

/**
 * Factory function to create a data layer registration hook for different data layer types
 */
export const createDataLayerRegister = () => {
  return () => {
    const { baseProps, linkProps, pageProps, itemProps, promoItemProps } =
      useBaseDataLayerVariables();

    /** Registers an action in the data layer with base properties */
    const registerAction = (
      data: DataLayerEvent = {},
      additionalBaseProps: Record<string, unknown> = {}
    ): Record<string, unknown>[] | undefined => {
      if (!ensureDataLayerExists()) return;

      const eventData: DataLayerEvent = {
        event: data.event || 'base_event',
        ...baseProps.value,
        timestamp: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        ...additionalBaseProps,
        ...data,
      };

      const finalPayload = processDataLayerObject(eventData, true) as Record<string, unknown>;

      globalThis.dataLayer.push(finalPayload);
      return globalThis.dataLayer;
    };

    /** Registers a user interaction event reusing registerAction with link properties */
    const registerInteraction = (
      data: DataLayerEvent = {}
    ): Record<string, unknown>[] | undefined => {
      // Use registerAction, merging linkProps as additional base properties
      return registerAction({ event: data.event || 'user_interaction', ...data }, linkProps.value);
    };

    /** Registers a page view event reusing registerAction with page properties */
    const registerPageView = (data: DataLayerEvent = {}): Record<string, unknown>[] | undefined => {
      // Use registerAction, merging pageProps as additional base properties
      return registerAction({ event: data.event || 'spa_page_view', ...data }, pageProps.value);
    };

    /** Registers a list view event, adding itemProps to each item in ecommerce.items */
    const registerListView = (data: DataLayerEvent = {}): Record<string, unknown>[] | undefined => {
      const eventData: EcommerceEvent = {
        event: data.event || 'view_item_list',
        ...baseProps.value,
        ...data,
      };

      // If ecommerce.items is an array, merge itemProps into each item
      if (eventData.ecommerce && Array.isArray(eventData.ecommerce.items)) {
        eventData.ecommerce.items = eventData.ecommerce.items.map((item) => ({
          ...itemProps.value,
          ...item,
        }));
      }

      return registerAction({ ...eventData });
    };

    const registerPromotionEvent = (
      data: DataLayerEvent = {}
    ): Record<string, unknown>[] | undefined => {
      const eventData: EcommerceEvent = {
        ...baseProps.value,
        ...data,
      };

      if (eventData.ecommerce && Array.isArray(eventData.ecommerce.items)) {
        eventData.ecommerce.items = eventData.ecommerce.items.map((item) => ({
          ...promoItemProps.value,
          ...item,
        }));
      }

      return registerAction({ ...eventData });
    };

    return {
      registerAction,
      registerInteraction,
      registerPageView,
      registerListView,
      registerPromotionEvent,
    };
  };
};

// Create specific data layer registrars
export const useDefaultDataLayer = createDataLayerRegister();
